import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AmplitudeService } from '@brighthub/logging';
import { ClientSubjectData } from '@brighthub/uisession';

// ---------------------------------------------------------------------------
// Contact model
// ---------------------------------------------------------------------------

export interface ClientContact {
  mobilePhone: string;
  workPhone: string;
  personalEmail: string;
  workEmail: string;
  /** Mobile phone preferred over work phone. */
  primaryPhone: string;
  /** Personal email preferred over work email. */
  primaryEmail: string;
}

export type ContactOrigin =
  | 'queue_menu'
  | 'metadata_links'
  | 'profile_contact'
  | 'journey_modal';

// ---------------------------------------------------------------------------
// Deterministic seed helpers — identical formulas to ClientProfileService
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function seedPick<T>(guid: string, key: string, options: readonly T[]): T {
  return options[djb2(guid + key) % options.length];
}

function seedDigits(guid: string, key: string, digits: number): string {
  const max = Math.pow(10, digits);
  const min = Math.pow(10, digits - 1);
  return String(min + (djb2(guid + key) % (max - min)));
}

// ---------------------------------------------------------------------------
// Lookup tables — same as ClientProfileService
// ---------------------------------------------------------------------------

const EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'protonmail.com',
] as const;

const COMPANY_DOMAINS = [
  'apexsolutions.com', 'blueridgepartners.com', 'crestviewind.com',
  'dunecapital.com', 'evergreenco.com', 'fairwaytech.com',
  'granitefinancial.com', 'harborwealth.com', 'ironwoodgroup.com',
  'juniperana.com',
] as const;

const FIRST_NAMES = [
  'James', 'Mary', 'Robert', 'Patricia', 'Michael', 'Jennifer', 'William',
  'Linda', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Lisa',
] as const;

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez',
  'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
] as const;

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class ClientContactService {
  private readonly _messageService = inject(MessageService);
  private readonly _amplitude = inject(AmplitudeService);

  /**
   * Resolve phone and email contact details for a client.
   * Uses the same deterministic seeding as ClientProfileService so values are
   * consistent across queue, metadata, and profile views.
   */
  resolveContact(s: ClientSubjectData): ClientContact {
    const g = s.guid;

    const firstName = s.firstName || seedPick(g, 'firstName', FIRST_NAMES);
    const lastName  = s.lastName  || seedPick(g, 'lastName',  LAST_NAMES);
    const companyDomain = seedPick(g, 'companyDomain', COMPANY_DOMAINS);

    const firstNameLower = firstName.toLowerCase();
    const lastNameLower  = lastName.toLowerCase();

    const workPhone   = `(${seedDigits(g, 'workPhoneArea', 3)}) ${seedDigits(g, 'workPhonePre', 3)}-${seedDigits(g, 'workPhoneLine', 4)}`;
    const mobilePhone = `(${seedDigits(g, 'mobileArea', 3)}) ${seedDigits(g, 'mobilePre', 3)}-${seedDigits(g, 'mobileLine', 4)}`;
    const personalEmail = `${firstNameLower}.${lastNameLower}@${seedPick(g, 'emailDomain', EMAIL_DOMAINS)}`;
    const workEmail     = `${firstNameLower}.${lastNameLower}@${companyDomain}`;

    return {
      mobilePhone,
      workPhone,
      personalEmail,
      workEmail,
      primaryPhone: mobilePhone,
      primaryEmail: personalEmail,
    };
  }

  /**
   * Normalize a phone string for use in a tel: link.
   * Strips spaces, parentheses, dashes; preserves a leading '+'.
   */
  normalizePhone(phone: string): string {
    const hasPlus = phone.trimStart().startsWith('+');
    const digits = phone.replace(/[^\d]/g, '');
    return hasPlus ? `+${digits}` : digits;
  }

  /**
   * Open a tel: link for the given phone number.
   * Shows a toast if the value is missing or clearly invalid.
   */
  openPhone(phone: string, origin?: ContactOrigin, subjectGuid?: string): void {
    const normalized = this.normalizePhone(phone ?? '');
    if (!normalized || normalized.length < 7) {
      this._messageService.add({
        severity: 'warn',
        summary: 'No phone available',
        detail: 'No valid phone number found for this client.',
        life: 4000,
      });
      return;
    }

    this._amplitude.logEvent('contactAction', {
      action: 'call',
      origin: origin ?? 'unknown',
      subjectGuid: subjectGuid ?? 'unknown',
    });

    window.location.href = `tel:${normalized}`;
  }

  /**
   * Open a mailto: link for the given email address.
   * Shows a toast if the value is missing or clearly invalid.
   */
  openEmail(email: string, origin?: ContactOrigin, subjectGuid?: string): void {
    if (!email?.includes('@')) {
      this._messageService.add({
        severity: 'warn',
        summary: 'No email available',
        detail: 'No valid email address found for this client.',
        life: 4000,
      });
      return;
    }

    this._amplitude.logEvent('contactAction', {
      action: 'email',
      origin: origin ?? 'unknown',
      subjectGuid: subjectGuid ?? 'unknown',
    });

    window.location.href = `mailto:${email}`;
  }
}
